import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, API_BASE } from "@/lib/kfmApi";
import { captureUtm, utmQuery } from "@/lib/utm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1721937539915-fcdda3e5dea8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | signup | forgot

  useEffect(() => {
    captureUtm();
    const err = new URLSearchParams(window.location.search).get("error");
    if (err) {
      toast.error("Google sign-in didn't complete. Please try again.");
      window.history.replaceState({}, "", "/login");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    if (mode === "login") {
      const res = await login(email, password);
      setLoading(false);
      if (res.ok) navigate("/study");
      else setError(res.error);
    } else if (mode === "signup") {
      const res = await register(email, password, name);
      setLoading(false);
      if (res.ok) navigate("/study");
      else setError(res.error);
    } else {
      try {
        const { data } = await api.post("/auth/forgot-password", { email });
        toast.success(data.message || "If that email exists, a reset link was sent.");
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // Custom Google OAuth with our own client — starts the server-side flow.
    window.location.href = `${API_BASE}/auth/google/login${utmQuery()}`;
  };

  const headings = {
    login: "Come read it for yourself.",
    signup: "Create your account",
    forgot: "Reset your password",
  };
  const taglines = {
    login: "No fluff. No commentary. Just Scripture, the way it was written.",
    signup: "Free to join. Your own studies, history, and virtual study companion.",
    forgot: "Enter your email and we'll send a reset link.",
  };
  const cta = { login: "Let's get started", signup: "Create account", forgot: "Send reset link" };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center relative overflow-hidden bg-[#07090E] px-6 py-10">
      <div className="absolute inset-0">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-25 blur-[2px] scale-110" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#07090E]/60 via-[#07090E]/80 to-[#07090E]" />
      </div>

      <div className="relative w-full max-w-sm animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="text-center mb-8">
          <img src="/openword-logo-horizontal.png" alt="OpenWord AI" className="h-8 mx-auto mb-5" />
          <h1 className="text-3xl text-white leading-tight">{headings[mode]}</h1>
          <p className="text-sm text-slate-400 mt-3 leading-relaxed">{taglines[mode]}</p>
        </div>

        {mode !== "forgot" && (
          <>
            <Button
              type="button"
              data-testid="google-login"
              onClick={googleLogin}
              variant="outline"
              className="w-full h-12 rounded-full bg-[#0E131F] border-[#29344D] text-slate-300 hover:bg-[#0B0F16] gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
                <path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 4.75 12 4.75z" />
              </svg>
              Continue with Google
            </Button>
            <div className="flex items-center gap-3 my-5">
              <div className="h-px flex-1 bg-[#1E273D]" />
              <span className="text-xs text-slate-500">or</span>
              <div className="h-px flex-1 bg-[#1E273D]" />
            </div>
          </>
        )}

        <form onSubmit={submit} className="space-y-4">
          {mode === "signup" && (
            <Input
              data-testid="signup-name"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-12 rounded-xl border-[#29344D] bg-[#0E131F]/90"
            />
          )}
          <Input
            data-testid="login-email"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="h-12 rounded-xl border-[#29344D] bg-[#0E131F]/90"
          />
          {mode !== "forgot" && (
            <Input
              data-testid="login-password"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-12 rounded-xl border-[#29344D] bg-[#0E131F]/90"
            />
          )}

          {error && <p data-testid="login-error" className="text-sm text-red-500 text-center">{error}</p>}

          <Button
            data-testid="auth-submit"
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-base font-medium"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : cta[mode]}
          </Button>
        </form>

        <div className="text-center mt-6 space-y-2">
          {mode === "login" && (
            <>
              <button data-testid="to-signup" type="button" onClick={() => { setMode("signup"); setError(""); }} className="block w-full text-sm text-slate-400 hover:text-[#00E5FF] transition-colors">
                New here? <span className="text-[#00E5FF]">Create an account</span>
              </button>
              <button data-testid="to-forgot" type="button" onClick={() => { setMode("forgot"); setError(""); }} className="block w-full text-sm text-slate-500 hover:text-[#00E5FF] transition-colors">
                Forgot your password?
              </button>
            </>
          )}
          {mode !== "login" && (
            <button data-testid="to-login" type="button" onClick={() => { setMode("login"); setError(""); }} className="text-sm text-slate-400 hover:text-[#00E5FF] transition-colors">
              Back to sign in
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
